import classnames from 'classnames';
import React, { CSSProperties } from 'react';
import { useRecoilValue } from 'recoil';
import { Color } from '../enums/Color.enum';
import { Fit } from '../enums/Fit.enum';
import { SelectionType } from '../enums/SelectionType.enum';
import { VariantStatus } from '../enums/VariantStatus.enum';
import { useProductSelection } from '../hooks/useProductSelection';
import { Preorder } from '../models/Preorder.model';
import { ViewVariant } from '../models/ShopifyVariant.model';
import { ViewProduct } from '../models/ViewProduct.model';
import { LongSize, ShortSize } from '../product_detail/components/size.enum';
import { preorderState } from '../recoil/atoms';
import { getPreorder, isRecoilProps, variantStatus } from '../util';
import styles from './ProductSizeSwatches.module.scss';

type Props = RecoilProps | CallbackProps;

interface RecoilProps extends BaseProps {
  selectionType: SelectionType;
  selectionKey: string;
}

interface CallbackProps extends BaseProps {
  selectedSize: LongSize | undefined;
  setSelectedSize: (size: LongSize) => void;
  color: Color | undefined;
  fit: Fit | undefined;
  useSmallSwatches?: boolean;
}

interface BaseProps {
  product: ViewProduct;
  onSelect?: (size: LongSize, fit: Fit | undefined, isAvailable: boolean) => void;
  style?: CSSProperties;
}

const getAvailability = ({
  preorders,
  color,
  size,
  fit,
  variants,
}: {
  size: LongSize;
  color: Color | undefined;
  fit: Fit | undefined;
  preorders: Preorder[];
  variants: ViewVariant[];
}) => {
  const variant = variants.find((v) => v.color === color && v.size === size && (!v.fit || v.fit === fit));
  const preorder = variant && getPreorder(preorders, variant);
  const status = variant && variantStatus(preorder, variant);

  switch (true) {
    case status === VariantStatus.Available:
    case status === VariantStatus.PreOrder:
      return true;
    default:
      return false;
  }
};

export const ProductSizeSwatches: React.FC<Props> = (props) => {
  return isRecoilProps<RecoilProps>(props) ? <RecoilProductSizeSwatches {...props} /> : <CallbackProductSizeSwatches {...props} />;
};

const CallbackProductSizeSwatches: React.FC<CallbackProps> = ({ style, product, selectedSize, setSelectedSize, color, fit, onSelect, useSmallSwatches }) => {
  const preorders = useRecoilValue(preorderState);
  if (!product.sizes?.length) return <></>;

  return (
    <div className={styles.sizeSwatchContainer} style={style}>
      <div className={styles.sizeSwatchWrapper}>
        {product.sizes.map((s, i) => {
          const isAvailable = getAvailability({ preorders, color, size: s as LongSize, fit, variants: product.variants });

          return (
            <SizeSwatch
              key={i}
              active={selectedSize === s}
              activate={() => {
                onSelect?.(s, fit, isAvailable);
                setSelectedSize(s);
              }}
              size={s}
              style={{ width: Math.floor((useSmallSwatches ? 300 : 350) / product.sizes.length) }}
              available={isAvailable}
            />
          );
        })}
      </div>
    </div>
  );
};

const RecoilProductSizeSwatches: React.FC<RecoilProps> = ({ product, selectionType, selectionKey, style, onSelect }) => {
  const { size: selectedSize, setSize: setSelectedSize, color, fit } = useProductSelection(selectionType, selectionKey);

  if (!product.sizes?.length) return <></>;

  return (
    <CallbackProductSizeSwatches
      color={color}
      fit={fit}
      product={product}
      selectedSize={selectedSize}
      setSelectedSize={setSelectedSize}
      style={style}
      onSelect={onSelect}
    />
  );
};

interface SizeSwatchProps {
  idPrefix?: string;
  size: LongSize;
  active: boolean;
  available: boolean;
  activate: (size: LongSize) => void;
  style?: CSSProperties;
}

const SizeSwatch: React.FC<SizeSwatchProps> = ({ size, active, available, activate, idPrefix, style }) => {
  const id = `${idPrefix ?? ''}size-swatch-${size}`;

  return (
    <button
      key={size}
      className={classnames(styles.swatch, { [styles.selected]: active, [styles.disabled]: !available })}
      id={id}
      role="radio"
      aria-label={available ? `${size}` : `${size} is out of stock`}
      aria-checked={active ? 'true' : 'false'}
      onClick={() => !active && activate(size)}
      style={style}
    >
      {(ShortSize as any)[size]}
    </button>
  );
};

export default ProductSizeSwatches;
